import {
  Box,
  cn,
  Flex,
  KdsFocusableElement,
  KdsText,
} from "@kazaar-platform/design-system";
import {
  RiCheckboxCircleFill,
  RiCloseCircleFill,
  RiIndeterminateCircleFill,
} from "@remixicon/react";
import dayjs from "dayjs";
import { useFormContext, useWatch } from "react-hook-form";
import { RetroplanningStatusEnum } from "@/config/apiClient.types";
import { type useGetRetroplanningStatusAvailability } from "@/hooks/useGetRetroplanningStatusAvailability";
import { tolgee } from "@/providers/TranslationProvider";
import { type InferredRetroplanningFormPageSchema } from "../../RetroplanningFormPage.schema";
import { type RetroplanningStepProps } from "./RetroplanningStep.types";

export const RetroplanningStep = ({
  item,
  isCurrentStep,
  onClick,
}: RetroplanningStepProps) => {
  const methods = useFormContext<InferredRetroplanningFormPageSchema>();

  const watchedDate = useWatch({
    control: methods.control,
    name: item.status,
  });

  const getStatusDescription = (
    item: ReturnType<typeof useGetRetroplanningStatusAvailability>[number],
    watchedDate: Date | null | undefined
  ): string => {
    if (item.disabled) {
      return tolgee.t("cardRetroplanning.alreadyDone");
    }

    if (watchedDate) {
      return tolgee.t("cardRetroplanning.beforeDate", {
        date: dayjs(watchedDate).format("L"),
      });
    }

    return tolgee.t("cardRetroplanning.noBeforeDate");
  };

  const getStatusIcon = (
    item: ReturnType<typeof useGetRetroplanningStatusAvailability>[number],
    watchedDate: Date | null | undefined,
    hasError: boolean
  ) => {
    const iconClassNames = cn(
      "size-si-sm",
      hasError && "text-error-main",
      watchedDate && "text-success-main",
      !watchedDate && !hasError && "text-text-bodyvariant",
      item.disabled && "text-transparent"
    );

    if (hasError) {
      return <RiCloseCircleFill className={iconClassNames} />;
    }

    if (watchedDate) {
      return <RiCheckboxCircleFill className={iconClassNames} />;
    }

    return <RiIndeterminateCircleFill className={iconClassNames} />;
  };

  const getTextColor = (
    item: ReturnType<typeof useGetRetroplanningStatusAvailability>[number]
  ): "error-text" | "text-bodyvariant" => {
    return methods.formState.errors[`${item.status}`]
      ? "error-text"
      : "text-bodyvariant";
  };

  const getStatusTranslation = () => {
    if (item.status === RetroplanningStatusEnum.QUOTE_TO_VALIDATE) {
      return tolgee.t("cardRetroplanning.status.QUOTE_TO_VALIDATE");
    }
    if (item.status === RetroplanningStatusEnum.HD_FILE_MISSING) {
      return tolgee.t("cardRetroplanning.status.HD_FILE_MISSING");
    }
    if (item.status === RetroplanningStatusEnum.PROOF_TO_VALIDATE) {
      return tolgee.t("cardRetroplanning.status.PROOF_TO_VALIDATE");
    }
    if (item.status === RetroplanningStatusEnum.PURCHASE_ORDER_MISSING) {
      return tolgee.t("cardRetroplanning.status.PURCHASE_ORDER_MISSING");
    }
    return "";
  };

  return (
    <KdsFocusableElement
      onClick={() => !item.disabled && onClick(item.status)}
      className="w-full"
    >
      <Box
        w="100%"
        rounded="br-sm"
        bgColor={isCurrentStep ? "primary.surface" : "bg.nobg"}
        opacity={item.disabled ? 0.4 : 1}
        cursor={item.disabled ? "not-allowed" : "pointer"}
        py="sp-sm"
        px="sp-md"
        hidden={item.hidden}
      >
        <Flex
          direction="row"
          gap="sp-sm"
          justify="space-between"
          align="center"
        >
          <Flex direction="column" gap="sp-xs2">
            <KdsText size="subtitle-sm">{getStatusTranslation()}</KdsText>
            <KdsText size="body-sm" color={getTextColor(item)}>
              {getStatusDescription(item, watchedDate)}
            </KdsText>
          </Flex>

          {getStatusIcon(
            item,
            watchedDate,
            !!methods.formState.errors[`${item.status}`]
          )}

          <input
            hidden
            aria-label={item.status}
            type="date"
            {...methods.register(item.status, {
              valueAsDate: true,
            })}
          />
        </Flex>
      </Box>
    </KdsFocusableElement>
  );
};
